import React, { useCallback, useMemo, useState } from 'react';
import { IconSearch, IconUser } from '@tabler/icons-react';
import { pb } from '../../api/pocketbase';
import { getManagerColor, getManagerInitials } from '../../lib/utils';
import { MultiInput } from '../MultiInput';
import { Select, FetchOptionsResult, SelectOption } from '../Select';

const PER_PAGE = 10;

interface UserOption {
  id: string;
  name: string;
}

interface UserSearchInputSingleProps {
  mode?: 'single';
  value: string;
  selectedName: string;
  onSelect: (id: string, name: string) => void;
  onClear: () => void;
  name?: string;
  placeholder?: string;
  disabled?: boolean;
  includeBlocked?: boolean;
  portal?: boolean;
}

interface UserSearchInputMultiProps {
  mode: 'multi';
  value: string[];
  onChange: (ids: string[]) => void;
  name?: string;
  placeholder?: string;
  initialOptions?: SelectOption[];
  allowedUserIds?: string[];
  includeBlocked?: boolean;
}

type UserSearchInputProps = UserSearchInputSingleProps | UserSearchInputMultiProps;

const mergeOptions = (prev: SelectOption[], next: SelectOption[]) => {
  const map = new Map(prev.map(option => [option.value, option]));
  next.forEach(option => map.set(option.value, option));
  return Array.from(map.values());
};

const buildUserFilter = (term: string): string | undefined => {
  if (!term.trim()) return undefined;
  const safe = term.replace(/"/g, '');
  const safeLower = safe.toLowerCase();
  const safeTitle = safeLower.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  const variants = [...new Set([safe, safeLower, safeTitle])];
  return variants.map(option => `name ~ "${option}"`).join(' || ');
};

const renderUser = (option: SelectOption | null) => {
  if (!option) return null;
  const initials = getManagerInitials(option.label);
  const color = getManagerColor(option.value);
  return (
    <div className="user-search-select-content">
      <div
        className="mgr-av"
        style={{ background: color, width: 22, height: 22, fontSize: 10, flexShrink: 0 }}
      >
        {initials}
      </div>
      <span>{option.label}</span>
    </div>
  );
};

const UserSearchInput: React.FC<UserSearchInputProps> = (props) => {
  const initOpts = props.mode === 'multi' ? (props.initialOptions ?? []) : [];
  const [knownOptions, setKnownOptions] = useState<SelectOption[]>(initOpts);

  const allowedUserIds = props.mode === 'multi' ? props.allowedUserIds : undefined;
  const includeBlocked = props.includeBlocked ?? false;

  const fetchOptions = useCallback(async ({
    page,
    search,
  }: {
    page: number;
    search: string;
  }): Promise<FetchOptionsResult> => {
    const parts: string[] = [];
    if (!includeBlocked) parts.push('isBlocked=false');
    const termFilter = buildUserFilter(search);
    if (termFilter) parts.push(termFilter);
    if (allowedUserIds?.length) {
      parts.push(`(${allowedUserIds.map(id => `id="${id}"`).join(' || ')})`);
    }
    const result = await pb.collection('users').getList<UserOption>(page, PER_PAGE, {
      filter: parts.length ? parts.join(' && ') : undefined,
      sort: 'name',
      fields: 'id,name',
    });

    const items = result.items.map((option: UserOption) => ({
      value: option.id,
      label: option.name,
    }));

    setKnownOptions(prev => mergeOptions(prev, items));

    return {
      items,
      hasMore: result.page < result.totalPages,
    };
  // ponytail: allowedUserIds array changes ref when groups change; key-based remount in RuleModal handles reset
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(allowedUserIds)]);

  if (props.mode === 'multi') {
    return (
      <MultiInput
        options={knownOptions}
        fetchOptions={fetchOptions}
        value={props.value}
        onChange={props.onChange}
        name={props.name}
        placeholder={props.placeholder ?? 'Поиск менеджера...'}
        searchPlaceholder="Поиск менеджера..."
        icon={<IconUser size={15} stroke={1.9} />}
        renderOption={renderUser}
        renderValue={renderUser}
      />
    );
  }

  const { value, selectedName, onSelect, onClear, placeholder = 'Поиск по имени', disabled, portal } = props;

  const options = useMemo(() => {
    const selectedOption = value && selectedName
      ? [{ value, label: selectedName }]
      : [];
    return mergeOptions(selectedOption, knownOptions);
  }, [knownOptions, selectedName, value]);

  const handleChange = (nextValue: string) => {
    if (!nextValue) {
      onClear();
      return;
    }
    const option = options.find(item => item.value === nextValue);
    onSelect(nextValue, option?.label ?? selectedName);
  };

  return (
    <Select
      className={`user-search-select${value ? ' has-value' : ''}`}
      options={options}
      value={value}
      onChange={handleChange}
      name={props.name}
      placeholder={placeholder}
      searchPlaceholder={placeholder}
      fetchOptions={fetchOptions}
      icon={value ? undefined : <IconSearch size={14} stroke={2} />}
      renderValue={renderUser}
      renderOption={(option: SelectOption) => renderUser(option)}
      disabled={disabled}
      portal={portal}
    />
  );
};

export { UserSearchInput };
